import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';
import EventEmitter from 'eventemitter3';
import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import * as syncProtocol from 'y-protocols/sync';
import * as Y from 'yjs';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { updateServerTimeOffset } from '@canvas/canvas-state/timer';
import { type Capabilities } from '@canvas/capability';
import {
  aPersonCanSaveUpdates,
  aPersonCanUseWhiteboards,
} from '@canvas/capability-summaries';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { updateIndexeddbStatus, waitForHealthCheck } from '@canvas/indexeddb-state';
import { AwarenessChannel, type AwarenessClientIDs } from '@canvas/model/awareness';
import { type ClientId } from '@canvas/model/telepointers';
import { MessageType } from '@canvas/protocol';
import {
  type AwarenessMessage,
  type SyncPhase0Message,
  type SyncPhase1Message,
  type SyncPhase2Message,
  type UpdateMessage,
  UpdateMessageOrigin,
  type LargeUpdateMessage,
  type SessionStatusMessage,
} from '@canvas/protocol-types';
import {
  isBackendAgent,
  isLayoutShift,
  isResizing,
  isUndoRedo,
} from '@canvas/protocol/messages';
import { encodeMessageV2, decodeMessageV2 } from '@canvas/protocol/v2';
import { isValidReceivedMessage } from '@canvas/protocol/validate-received-message';
import { UnrecoverableNetworkError } from '@canvas/retry';
import {
  captureException,
  CustomerImpact,
  captureExceptionForCapability,
} from '@canvas/sentry-react';
import {
  CanvasTransactionOrigin,
  BackendAgentOrigin,
  getUpdateMessageOrigin,
  type TransactionOrigin,
  YjsOrigin,
  YJS_DEFAULT,
  originIsFrontendAgent,
} from '@canvas/transaction';
import { IndexeddbPersistence } from '@canvas/yjs-providers/idb';
import { applyUpdate, safeApplyUpdate, validUpdateOrDie } from '@canvas/yjs-utils';

import { getCollabStrategy, type CollabStrategy } from '../collab-strategy/index.ts';
import { type CollabConnectionClientSender } from '../transport/collab-connection-transport.ts';
import { mergeUpdate } from '../util/merge-update.ts';

import { type Update, type AbstractBatchedUpdates } from './abstract-batched-updates.ts';
import { AwarenessContainer } from './awareness-container.ts';
import { BackendAgentOperationBuffer } from './backend-agent-operation-buffer.ts';
import { BatchedUpdates } from './batched-updates.ts';
import { debugLog } from './debug-logger.ts';
import { parseRelayUrl } from './document-steps.ts';
import {
  BoardDocumentMissingError,
  DocumentLockedError,
  type RemoteDocument,
} from './document.ts';
import { FatalConnectionError } from './errors.ts';
import { CloseConnectTimeGapNcsMetrics } from './http-bootstrap-metrics.ts';
import { fetchLargeStep } from './large-document-steps.ts';
import { LazySyncMetrics } from './lazy-sync-metrics.ts';
import { NoopTestSocket } from './no-op-socket.ts';
import type { Waiter } from './socket-reconnect-debounce.ts';
import {
  recordSocketRestart,
  type SocketRestartReason,
} from './socket-restart-metrics.ts';
import {
  AuthenticatedSocket,
  type SocketInterface,
  type SocketFactory,
  type RelaySupplierResult,
} from './socket.ts';
import { StateBridge } from './state-bridge.ts';
import { UpdateTracker } from './update-tracker.ts';
import { WebSocketCodes } from './websocket-codes.ts';

export type { Waiter };

type RemoteStepsUpdate = (
  step: string,
  attempt: number,
  signal: AbortSignal,
  collabStrategy: CollabStrategy,
) => Promise<Uint8Array[]>;

export type DocumentStatusResult = {
  latestStep: string | null;
  timestamp: number;
} | null;

export type GetDocumentStatusFn = (signal: AbortSignal) => Promise<DocumentStatusResult>;

export interface YjsSocketOptions {
  autoConnect?: boolean;
  accessTokenSupplier: () => Promise<string>;
  relaySupplier: (
    attempt: number,
    signal: AbortSignal,
    collabStrategy: CollabStrategy,
  ) => RelaySupplierResult;
  doc: Y.Doc;
  getDocument: (signal: AbortSignal) => Promise<RemoteDocument>;
  persistLargeStep: (step: Uint8Array<ArrayBuffer>, signal: AbortSignal) => Promise<void>;
  reconcileOfflineChanges?: (remoteDocument: RemoteDocument) => Promise<void>;
  ignoreOrigins: any[];
  updateTracker?: UpdateTracker;
  debounceWaiter?: Waiter;
  socketFactory?: SocketFactory;
  getAnalyticsWebClient?: () => Promise<AnalyticsWebClient>;
  canSync?: boolean;
  canUpdate?: boolean;
  indexedDbEnabled?: boolean;
  /**
   * Triggered when the socket is closed. Reconnects may still take place after
   * it has closed. Used this hook to execute logic after a socket close event.
   */
  onClosed?: (reason: WebSocketCodes) => void;
  boardId: string;
  getRemoteStepsUpdate: RemoteStepsUpdate;
  getDocumentStatus?: GetDocumentStatusFn;
  signal?: AbortSignal;
  getCapabilities?: () => Capabilities | undefined;
  /**
   * The version of the document to fetch.
   * If fetching a specific version, the socket will not attempt to update the
   * document with recent steps.
   */
  versionId?: string;
  /**
   * Determines whether or not version history is enabled for whiteboards or database
   */
  useVersionHistory?: boolean;
  /**
   * Called each time a step roundtrip completes — i.e. when a local Yjs update
   * is confirmed/echoed back by the server. Both timestamps are `Date.now()`
   * epoch milliseconds, so latency is simply `receivedAt - sentAt`.
   *
   * Works for both NCS mode (fired via `StateBridge.confirm`) and CF mode
   * (fired via `UpdateTracker.received`). Intended for backend load-test
   * runners (e.g. socketio-simulator) that need to emit roundtrip latency to
   * StatsD/SignalFx. The browser-side `ReliabilityMetrics` / UFO pipeline is
   * a no-op in Node.js, so this is the correct hook for server-side metric
   * collection.
   */
  onRoundtrip?: (sentAt: number, receivedAt: number) => void;
}

interface YjsSocketEvents {
  initialLoad: () => void;
  connected: () => void;
  disconnected: () => void;
  /**
   * Fired when the socket fails to connect due to the maximum collaborator count being
   * reached.
   */
  sessionFull: () => void;
  /**
   * Fired when the board is temporarily locked and cannot accept new connections.
   * The client will continue to retry and will reconnect automatically when the
   * lock is released or its TTL elapses.
   */
  sessionLocked: () => void;
  connecting: (attempt: number) => void;
  fatalError: (error: Error) => void;
  failingRetry: (error: Error) => void;
  closed: (code: WebSocketCodes) => void;
  indexedDbSynced: () => void;
}

export type LocalPersistenceState = 'pending' | 'ready' | 'unavailable';

export interface UpdateMetadata {
  /**
   * Yjs clientID associated with the update. Should be the same for an entire session,
   * however Y.js can change it on the fly if a conflict with another client ends up
   * happening.
   */
  clientID: number;
  /**
   * Yjs clock value for the above clientID at the end of the update.
   */
  clock: number;
  /**
   * The origin of the update. We just take the origin of the last received update.
   */
  origin: TransactionOrigin;
}

/**
 * Durable Objects have an inbound websocket message limit of 1 MiB (1048576 bytes)
 * @see https://developers.cloudflare.com/workers/platform/limits/#durable-objects
 */
export const LARGE_STEP_THRESHOLD = 1_048_576;
export const LAZY_SYNC_TIMEOUT_MS = 30_000;
export const BACKEND_AGENT_OPERATION_TIMEOUT_MS = 30_000;

// Time allowed for local clock to be out to allow for network latency
const SERVER_TIME_DRIFT_THRESHOLD = 300;

function getDocumentId(relayUrl: string) {
  try {
    return parseRelayUrl(relayUrl).documentId;
  } catch (err) {
    return undefined;
  }
}

function encodeStateAsUpdateWithoutPendingData(
  doc: Y.Doc,
  remoteStateVector: Uint8Array,
) {
  const pendingDs = doc.store.pendingDs;
  const pendingStructs = doc.store.pendingStructs;

  try {
    doc.store.pendingDs = null;
    doc.store.pendingStructs = null;

    return new Uint8Array(Y.encodeStateAsUpdate(doc, remoteStateVector));
  } finally {
    doc.store.pendingDs = pendingDs;
    doc.store.pendingStructs = pendingStructs;
  }
}

export const calculateUpdateOrigin = (originFlags: number) => {
  if (isUndoRedo(originFlags)) {
    return CanvasTransactionOrigin.RemoteUndoManager;
  }
  if (isResizing(originFlags)) {
    return CanvasTransactionOrigin.Resizing;
  }
  if (isLayoutShift(originFlags)) {
    return CanvasTransactionOrigin.LayoutShift;
  }
  if (isBackendAgent(originFlags)) {
    return CanvasTransactionOrigin.BackendAgent;
  }
  return YJS_DEFAULT;
};

export class YjsSocket {
  // Stores the offline diff computed in onSocketUpdateChanges so it can be
  // loaded into the StateBridge after the socket is fully connected (i.e. after
  // the C3 'connected' event fires and the NCS namespace join is complete).
  // Sending via sendThenConfirm before 'connected' fires causes the sendWithAck
  // packet to be dropped by NCS because socket permissions aren't set up yet.
  private pendingReconnectUpdate: {
    update: Uint8Array<ArrayBuffer>;
    clock: number;
  } | null = null;
  public awarenessContainer: AwarenessContainer;
  public socket: SocketInterface;
  public doc: Y.Doc;
  public readonly updateTracker: UpdateTracker;

  private stateBridge = new StateBridge();
  private externalOnRoundtrip?: (sentAt: number, receivedAt: number) => void;
  private batchedUpdates: AbstractBatchedUpdates<
    Uint8Array<ArrayBuffer>,
    UpdateMetadata
  > = new BatchedUpdates<Uint8Array<ArrayBuffer>, UpdateMetadata>(
    200,
    mergeUpdate.bind(this),
  );
  private batchedAwarenessUpdates = new BatchedUpdates<
    /**
     * Maps an awareness channel to the client IDs that have updated it. The client ID will
     * almost always just be the local user.
     */
    Map<AwarenessChannel, Set<number>>
  >(200, this.mergeAwareness.bind(this));

  private lastSentClock: number | null = null;
  private events: EventEmitter<YjsSocketEvents> = new EventEmitter<YjsSocketEvents>();
  private readonly backendAgentOperationBuffer = new BackendAgentOperationBuffer();
  private readonly backendAgentOperationTimeouts = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly isBackendAgentOperationBufferingEnabled: boolean;
  private readonly getDocument: (signal: AbortSignal) => Promise<RemoteDocument>;
  private readonly ignoreOrigins: any[];
  private persistLargeStep: (
    step: Uint8Array<ArrayBuffer>,
    signal: AbortSignal,
  ) => Promise<void>;
  private documentLookupFailed: boolean;
  private canSync: boolean;
  private canUpdate: boolean;
  private localPersistenceStateValue: LocalPersistenceState;
  private getRemoteStepsUpdate: RemoteStepsUpdate;
  private getDocumentStatusFn?: GetDocumentStatusFn;
  private getCapabilities?: () => Capabilities | undefined;
  private headStep: string | null = null;
  private collabStrategy: CollabStrategy = getCollabStrategy('cf');

  // vars used to track lazy sync status
  private lastUpdateSender: CollabConnectionClientSender | null = null;
  private lazySyncTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private lazySyncMetrics = new LazySyncMetrics({
    timeoutMs: LAZY_SYNC_TIMEOUT_MS,
  });

  private get lazySyncInFlight() {
    return this.lazySyncTimeoutHandle !== null;
  }

  constructor({
    accessTokenSupplier,
    relaySupplier,
    doc,
    getDocument,
    persistLargeStep,
    autoConnect,
    ignoreOrigins,
    updateTracker,
    debounceWaiter,
    socketFactory,
    getAnalyticsWebClient,
    canSync = true,
    canUpdate = true,
    indexedDbEnabled = true,
    boardId,
    getRemoteStepsUpdate,
    getDocumentStatus,
    signal,
    getCapabilities,
    versionId,
    reconcileOfflineChanges,
    useVersionHistory = false,
    onRoundtrip,
  }: YjsSocketOptions) {
    this.awarenessContainer = new AwarenessContainer(doc);
    this.doc = doc;
    this.isBackendAgentOperationBufferingEnabled = isFeatureFlagEnabled(
      'agenticConfluenceFrontendEditUI',
    );
    this.updateTracker = updateTracker ?? new UpdateTracker(doc.clientID);
    this.externalOnRoundtrip = onRoundtrip;
    this.getDocument = getDocument;
    this.persistLargeStep = persistLargeStep;
    this.ignoreOrigins = [...ignoreOrigins];
    this.documentLookupFailed = false;
    this.canSync = canSync;
    this.canUpdate = canUpdate;
    this.getRemoteStepsUpdate = getRemoteStepsUpdate;
    this.getDocumentStatusFn = getDocumentStatus;
    this.getCapabilities = getCapabilities;

    const shouldSkipUpdates = () =>
      Boolean(versionId) && versionId !== 'latest' && useVersionHistory;

    // A migration must not inspect the document while IndexedDB may still
    // replay offline edits. Sessions without local persistence are ready from
    // the start; a failed health check remains unavailable and skips migration.
    const shouldInitializeLocalPersistence =
      indexedDbEnabled && Boolean(globalThis.indexedDB) && !shouldSkipUpdates();
    this.localPersistenceStateValue = shouldInitializeLocalPersistence
      ? 'pending'
      : 'ready';

    this.socket =
      process.env.MODE === 'test'
        ? new NoopTestSocket()
        : new AuthenticatedSocket({
            accessTokenSupplier,
            relaySupplier,
            onSocketFetchSnapshot: this.onSocketFetchSnapshot.bind(this),
            onSocketFetchSteps: this.onSocketFetchSteps.bind(this),
            onSocketApplyChanges: this.onSocketApplyChanges.bind(this),
            onSocketUpdateChanges: this.onSocketUpdateChanges.bind(this),
            onSocketClosed: this.onSocketClosed.bind(this),
            onAckTimeout: this.onAckTimeout,
            debounceWaiter,
            shouldHaltRetry: this.shouldHaltRetry.bind(this),
            socketFactory,
            getAnalyticsWebClient,
            stateBridge: this.stateBridge,
            signal,
            shouldSkipUpdates,
            reconcileOfflineChanges: async (doc) => {
              await reconcileOfflineChanges?.(doc);
              // Ensure this update is immediately flushed to peers
              this.batchedUpdates.flush();
            },
          });

    this.socket.on('connected', this.onSocketConnect);
    this.socket.on('disconnected', this.onSocketDisconnect);
    this.socket.on('message', this.onMessage);
    this.socket.on('transientMessage', this.onTransientMessage);
    this.socket.on('failingRetry', (err: Error) => this.events.emit('failingRetry', err));
    this.socket.on('connecting', (attempt: number) =>
      this.events.emit('connecting', attempt),
    );

    this.batchedUpdates.onUpdate(this.onBatchedUpdate);
    this.batchedUpdates.onFlush(this.flushTransport);
    this.batchedAwarenessUpdates.onUpdate(this.onBatchedAwarenessUpdate);
    this.doc.on('update', this.updateHandler);
    this.awarenessContainer.on('update', this.awarenessUpdateHandler);

    if (globalThis.addEventListener !== undefined) {
      globalThis.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else if (typeof process !== 'undefined') {
      process.on('exit', () => this.beforeUnloadHandler);
    }

    if (autoConnect ?? true) {
      this.connect().catch((err) => {
        console.error('failed to connect', err);
        this.events.emit('fatalError', new FatalConnectionError(err));
      });
    }

    if (shouldInitializeLocalPersistence) {
      waitForHealthCheck()
        .then((isHealthy) => {
          if (isHealthy) {
            const indexedDb = new IndexeddbPersistence(
              boardId,
              doc,
              updateIndexeddbStatus,
            );
            this.ignoreOrigins.push(indexedDb);
            indexedDb?.on?.('synced', () => {
              this.localPersistenceStateValue = 'ready';
              this.events.emit('indexedDbSynced');
            });
          } else {
            this.localPersistenceStateValue = 'unavailable';
          }
        })
        .catch((error) => {
          this.localPersistenceStateValue = 'unavailable';
          captureException(error, {
            tags: {
              captureLocation: 'yjs-socket-indexeddb-healthcheck',
            },
          });
        });
    }
  }

  public async connect(): Promise<void> {
    await this.socket.connect().catch((err) => {
      console.error('failed to connect', err);
      this.events.emit('fatalError', new FatalConnectionError(err));
    });
  }

  /** Reports whether locally persisted updates are safe to include in migrations. */
  public get localPersistenceState(): LocalPersistenceState {
    return this.localPersistenceStateValue;
  }

  public pause(): void {
    this.socket.pause();
  }

  public resume(): void {
    this.socket.resume();
  }

  protected shouldHaltRetry() {
    return this.documentLookupFailed;
  }

  private isSyncMessage(type: MessageType) {
    return (
      type === MessageType.SyncInit ||
      type === MessageType.SyncPhase0 ||
      type === MessageType.SyncPhase1 ||
      type === MessageType.SyncPhase2
    );
  }

  private get activeCollabStrategy(): CollabStrategy {
    return this.collabStrategy;
  }

  private onTransientMessage = (event: Uint8Array<ArrayBuffer>) => {
    const message = decodeMessageV2(event);

    if (message.type !== MessageType.Awareness || !isValidReceivedMessage(message)) {
      captureException(
        new Error(`Received invalid transient message with type: ${message.type}`),
        {
          tags: {
            captureLocation: 'yjs-socket-invalid-transient-message',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
      );
      return;
    }

    this.onAwareness(message);
  };

  private onMessage = (
    event: Uint8Array<ArrayBuffer>,
    sender?: CollabConnectionClientSender,
  ) => {
    const message = decodeMessageV2(event);

    if (!this.activeCollabStrategy.isReceivedMessageValid(message)) {
      captureException(
        new Error(`Received invalid message with type: ${(message as any).type}`),
        {
          tags: {
            captureLocation: 'yjs-socket-invalid-received-message',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
      );
      return;
    }

    if (!this.checkCanSync() && this.isSyncMessage(message.type)) {
      return;
    }

    switch (message.type) {
      case MessageType.Update:
        this.onUpdate(message, sender);
        break;
      case MessageType.LargeUpdate:
        this.onLargeUpdate(message);
        break;
      case MessageType.SyncPhase0:
        this.onSyncPhase0(message, sender);
        break;
      case MessageType.SyncPhase1:
        this.onSyncPhase1(message);
        break;
      case MessageType.SyncPhase2:
        this.onSyncPhase2(message);
        break;
      case MessageType.Awareness:
        this.onAwareness(message);
        break;
      case MessageType.SessionStatus:
        this.onSessionStatus(message);
        break;
    }
  };

  private onSessionStatus({ data: { timestamp } }: SessionStatusMessage) {
    const offset = Date.now() - timestamp;
    if (Math.abs(offset) > SERVER_TIME_DRIFT_THRESHOLD) {
      updateServerTimeOffset(offset);
    }
  }

  /**
   * NCS path: close the connect-time gap by calling the HTTP document-status endpoint
   * and re-fetching any missed steps.
   */
  private async closeConnectTimeGapNcs() {
    const metrics = new CloseConnectTimeGapNcsMetrics();

    try {
      if (!this.getDocumentStatusFn) {
        throw new Error('YjsSocket missing getDocumentStatusFn');
      }
      const signal = new AbortController().signal;
      const status = await this.getDocumentStatusFn(signal);

      if (!status) {
        metrics.success('no_status');
        return;
      }

      // Apply clock drift
      const offset = Date.now() - status.timestamp;
      if (Math.abs(offset) > SERVER_TIME_DRIFT_THRESHOLD) {
        updateServerTimeOffset(offset);
      }

      // Re-fetch missed steps if there's a gap
      if (status.latestStep && this.headStep && status.latestStep > this.headStep) {
        const gapClosed = await this.applyGapClosingSteps(this.headStep);
        if (gapClosed) {
          metrics.success('gap_closed');
        } else {
          metrics.failure('failed to apply gap-closing steps');
        }
        return;
      }

      metrics.success('no_gap');
    } catch (err) {
      metrics.failure(err);
      captureException(err, {
        tags: {
          captureLocation: 'yjs-socket-close-connect-time-gap-ncs',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      });
    }
  }

  /**
   * Re-fetch and apply steps from headStep to close the connect-time gap.
   */
  private async applyGapClosingSteps(fromStep: string): Promise<boolean> {
    try {
      const signal = new AbortController().signal;
      const stepsUpdates = await this.getRemoteStepsUpdate(
        fromStep,
        0,
        signal,
        this.collabStrategy as CollabStrategy, // guaranteed to not be null, as applyGapClosingSteps should only be called after onSocketFetchSnapshot is called
      );

      let allStepsApplied = true;
      for (const update of stepsUpdates) {
        const stepApplied = safeApplyUpdate(this.doc, update, undefined, (err) =>
          captureExceptionForCapability(
            err,
            {
              tags: {
                captureLocation: 'gap-closing-steps-error-on-apply-update',
                customerImpact: CustomerImpact.CUSTOMERS,
              },
            },
            aPersonCanUseWhiteboards,
          ),
        );
        allStepsApplied = allStepsApplied && stepApplied;
      }

      return allStepsApplied;
    } catch (err) {
      captureException(err, {
        tags: {
          captureLocation: 'yjs-socket-apply-gap-closing-steps',
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      });
      return false;
    }
  }

  private resetLazySyncState() {
    if (this.lazySyncTimeoutHandle !== null) {
      clearTimeout(this.lazySyncTimeoutHandle);
      this.lazySyncTimeoutHandle = null;
    }
    this.lazySyncMetrics.reset();
    this.lastUpdateSender = null;
  }

  private hasPendingSyncState() {
    return (
      (this.doc.store.pendingStructs?.missing.size ?? 0) > 0 ||
      (this.doc.store.pendingDs?.length ?? 0) > 0
    );
  }

  /**
   * When a received update has missing dependencies (detected via doc.store.pendingStructs),
   * trigger a peer-to-peer sync handshake to recover the missing data from the sending client.
   *
   * In NCS mode there is no Durable Object to fan out a SyncInit — the DO was the only consumer
   * of that message and would respond with SyncPhase0 to all other peers. Instead we send
   * SyncPhase0 directly to the peer who sent the update that exposed the gap, which their
   * onSyncPhase0 handler already handles correctly by responding with SyncPhase1.
   */
  private triggerLazySyncIfNeeded() {
    if (this.lazySyncInFlight) {
      return;
    }

    if (!this.activeCollabStrategy.shouldHandleLazySync()) {
      return;
    }

    if (this.hasPendingSyncState()) {
      this.lazySyncTimeoutHandle = setTimeout(() => {
        this.lazySyncMetrics.timeout();
        this.restartSocketState('lazy_sync_timeout');
      }, LAZY_SYNC_TIMEOUT_MS);
      this.lazySyncMetrics.requestSent();

      const encoder = encoding.createEncoder();
      syncProtocol.writeSyncStep1(encoder, this.doc);
      const syncData = encoding.toUint8Array(encoder);

      this.activeCollabStrategy.sendLazySyncRequest({
        message: encodeMessageV2({
          type: MessageType.SyncPhase0,
          data: {
            request: syncData,
            recipient: '',
          },
        }),
        lastUpdateSender: this.lastUpdateSender,
        send: (message) => this.socket.send(message),
        sendToPeer: (message, peer) => this.socket.sendToPeer(message, peer),
      });
    }
  }

  private onUpdate(message: UpdateMessage, sender?: CollabConnectionClientSender) {
    if (!this.checkCanReceiveUpdate()) {
      return;
    }
    const { data, origin: originFlags } = message.data;
    const origin = calculateUpdateOrigin(originFlags);

    debugLog(
      '[YjsSocket:onUpdate] INCOMING update from network: socketState=%s, collabStrategy=%s, dataBytes=%d, sender=%s, originFlags=%d',
      this.socket.state,
      this.activeCollabStrategy.type,
      data.byteLength,
      sender ?? 'none',
      originFlags,
    );

    // Track the sender of the incoming update for targeted lazy sync
    this.lastUpdateSender = sender ?? null;

    // do not throw if received data is not valid
    // just capture the exception and leave the document unchanged
    try {
      validUpdateOrDie(data);

      this.updateTracker.received(data);

      let updateToApply = data;
      let transactionOrigin =
        message.data.origin === UpdateMessageOrigin.BackendAgent
          ? new BackendAgentOrigin(this as unknown as TransactionOrigin, {
              agentOwnerAccountId: message.data.agentOwnerAccountId,
              agentOperationId: message.data.agentOperationId,
            })
          : new YjsOrigin(origin, this as unknown as TransactionOrigin);

      if (
        this.isBackendAgentOperationBufferingEnabled &&
        message.data.origin === UpdateMessageOrigin.BackendAgent &&
        message.data.agentOperationId !== undefined &&
        message.data.agentOperationUpdateIndex !== undefined
      ) {
        const completedOperation = this.backendAgentOperationBuffer.addUpdate({
          operationId: message.data.agentOperationId,
          agentOperationUpdateIndex: message.data.agentOperationUpdateIndex,
          agentOperationUpdateCount: message.data.agentOperationUpdateCount,
          agentOwnerAccountId: message.data.agentOwnerAccountId,
          update: data,
        });
        if (completedOperation === undefined) {
          this.scheduleBackendAgentOperationTimeout(message.data.agentOperationId);
          return;
        }

        this.cancelBackendAgentOperationTimeout(message.data.agentOperationId);
        updateToApply = completedOperation.update;
        transactionOrigin = new BackendAgentOrigin(this as unknown as TransactionOrigin, {
          agentOwnerAccountId: completedOperation.agentOwnerAccountId,
          agentOperationId: completedOperation.operationId,
        });
      }

      applyUpdate(this.doc, updateToApply, transactionOrigin);

      if (
        this.activeCollabStrategy.shouldHandleLazySync() &&
        this.hasPendingSyncState()
      ) {
        this.lazySyncMetrics.unappliedUpdate({
          lazySyncInFlight: this.lazySyncInFlight,
        });
      }

      this.triggerLazySyncIfNeeded();
    } catch (err) {
      captureExceptionForCapability(
        err,
        {
          tags: {
            captureLocation: 'yjs-socket-on-update-catch-all',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanSaveUpdates,
      );
    }
  }

  private scheduleBackendAgentOperationTimeout(operationId: string): void {
    this.cancelBackendAgentOperationTimeout(operationId);
    this.backendAgentOperationTimeouts.set(
      operationId,
      setTimeout(() => {
        this.resetBackendAgentOperationBufferState();
        if (this.socket.open) {
          this.restartSocketState('backend_agent_operation_timeout');
        }
      }, BACKEND_AGENT_OPERATION_TIMEOUT_MS),
    );
  }

  private cancelBackendAgentOperationTimeout(operationId: string): void {
    const timeout = this.backendAgentOperationTimeouts.get(operationId);
    if (timeout !== undefined) {
      clearTimeout(timeout);
      this.backendAgentOperationTimeouts.delete(operationId);
    }
  }

  private resetBackendAgentOperationBufferState(): void {
    for (const timeout of this.backendAgentOperationTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.backendAgentOperationTimeouts.clear();
    this.backendAgentOperationBuffer.clear();
  }

  private onLargeUpdate(message: LargeUpdateMessage) {
    if (!this.checkCanReceiveUpdate()) {
      return;
    }
    const signedDownloadUrl = message.data.data;
    fetchLargeStep({ signedDownloadUrl })
      .then((binaryUpdate) => {
        this.updateTracker.received(binaryUpdate);
        applyUpdate(
          this.doc,
          binaryUpdate,
          new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin),
        );
      })
      .catch((e) =>
        captureExceptionForCapability(
          e,
          {
            tags: {
              captureLocation: 'onLargeUpdate-fetch-step-failed',
              customerImpact: CustomerImpact.CUSTOMERS,
            },
          },
          aPersonCanSaveUpdates,
        ),
      );
  }

  private checkCanSync() {
    if (!this.getCapabilities) {
      return this.canSync;
    }
    const capabilities = this.getCapabilities();
    return Boolean(capabilities?.updates.canConsume) && this.canSync;
  }

  private checkCanUpdate() {
    if (!this.getCapabilities) {
      return this.canUpdate;
    }
    const capabilities = this.getCapabilities();
    return capabilities?.updates.canSend && this.canUpdate;
  }

  private checkCanReceiveUpdate() {
    if (!this.getCapabilities) {
      return true;
    }
    const capabilities = this.getCapabilities();
    return capabilities?.updates.canConsume;
  }

  private onSyncPhase0 = (
    { data: { request, recipient } }: SyncPhase0Message,
    sender?: CollabConnectionClientSender,
  ) => {
    const decoder = decoding.createDecoder(request);
    const encoder = encoding.createEncoder();

    syncProtocol.readSyncMessage(
      decoder,
      encoder,
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin),
    );

    const syncReplyEncoder = encoding.createEncoder();
    syncProtocol.writeSyncStep1(syncReplyEncoder, this.doc);

    // In NCS mode, reply directly to the sender via sendToPeer rather than
    // broadcasting to all peers — the sync handshake is point-to-point.
    // Use the sender's peer ID as the new recipient so they know to reply back to us.
    // In CF mode, send() goes to the DO which routes via the recipient field.
    const replyRecipient = this.activeCollabStrategy.getSyncPhase1ReplyRecipient({
      recipient,
      sender,
    });

    const message = encodeMessageV2({
      type: MessageType.SyncPhase1,
      data: {
        recipient: replyRecipient,
        request: encoding.toUint8Array(syncReplyEncoder),
        response: encoding.toUint8Array(encoder),
      },
    });

    if (message.length > LARGE_STEP_THRESHOLD) {
      // Don't send sync message if it would cause the socket to terminate
      return;
    }

    this.activeCollabStrategy.sendSyncPhase1Reply({
      message,
      recipient,
      sender,
      send: (encodedMessage) => this.socket.send(encodedMessage),
      sendToPeer: (encodedMessage, peer) => this.socket.sendToPeer(encodedMessage, peer),
    });
  };

  private onSyncPhase1 = ({
    data: { request, response, recipient },
  }: SyncPhase1Message) => {
    const decoder = decoding.createDecoder(request);
    const encoder = encoding.createEncoder();

    syncProtocol.readSyncMessage(
      decoder,
      encoder,
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin),
    );

    syncProtocol.readSyncMessage(
      decoding.createDecoder(response),
      encoding.createEncoder(),
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin),
    );

    if (this.lazySyncInFlight) {
      this.lazySyncMetrics.completed();
      this.resetLazySyncState();
    }

    const message = encodeMessageV2({
      type: MessageType.SyncPhase2,
      data: {
        recipient,
        response: encoding.toUint8Array(encoder),
      },
    });

    if (message.length > LARGE_STEP_THRESHOLD) {
      // Don't send sync message if it would cause the socket to terminate
      return;
    }

    // In NCS mode, reply directly to the original requester (identified by the
    // recipient field set by the SyncPhase0 sender) rather than broadcasting.
    // In CF mode, send() goes to the DO which routes via the recipient field.
    this.activeCollabStrategy.sendSyncPhase2Reply({
      message,
      recipient,
      send: (encodedMessage) => this.socket.send(encodedMessage),
      sendToPeer: (encodedMessage, peer) => this.socket.sendToPeer(encodedMessage, peer),
    });
  };

  private onSyncPhase2 = ({ data: { response } }: SyncPhase2Message) => {
    const decoder = decoding.createDecoder(response);
    const encoder = encoding.createEncoder();

    syncProtocol.readSyncMessage(
      decoder,
      encoder,
      this.doc,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin),
    );

    this.resetLazySyncState();
  };

  private onAwareness = (message: AwarenessMessage) => {
    this.awarenessContainer.onAwareness(message, this, this.getCapabilities?.());
  };

  private updateHandler = (
    update: Uint8Array,
    origin: any,
    _doc: Y.Doc,
    transaction: Y.Transaction,
  ) => {
    const ignored = this.ignoreOrigins.includes(origin) || this.isCurrentOrigin(origin);
    debugLog(
      '[YjsSocket:updateHandler] socketState=%s, ignored=%s, collabStrategy=%s, updateBytes=%d, originType=%s',
      this.socket.state,
      ignored,
      this.activeCollabStrategy.type,
      update.byteLength,
      origin?.constructor?.name ?? typeof origin,
    );
    if (!ignored) {
      const clock =
        transaction.afterState.get(this.doc.clientID) ??
        Y.getState(this.doc.store, this.doc.clientID);

      this.batchedUpdates.addUpdate({
        payload: new Uint8Array(update),
        origin,
        clientID: this.doc.clientID,
        clock,
      });
    }
  };

  private onBatchedUpdate = (update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => {
    const isLargeStep = update.payload.length > LARGE_STEP_THRESHOLD;
    const signal = new AbortController().signal;
    if (isLargeStep) {
      this.persistLargeStep(update.payload, signal)
        .then(() => {
          this.updateTracker.sent(update.payload);
        })
        .catch((err) => {
          captureExceptionForCapability(
            err,
            {
              tags: {
                captureLocation: 'batched-update-persist-large-step',
                customerImpact: CustomerImpact.CUSTOMERS,
              },
            },
            aPersonCanSaveUpdates,
          );
        });
    } else {
      this.updateTracker.sent(update.payload);
      this.sendUpdate(
        update.payload,
        getUpdateMessageOrigin(update.origin),
        update.clock,
      );
    }
  };

  private onBatchedAwarenessUpdate = (
    update: Update<Map<AwarenessChannel, Set<number>>>,
  ) => {
    const message = encodeMessageV2(
      this.awarenessContainer.getAwarenessMessage(
        update.payload,
        this.getCapabilities?.(),
      ),
    );

    this.socket.sendAwareness(message);
  };

  private awarenessUpdateHandler = (
    channel: AwarenessChannel,
    { added, updated, removed }: AwarenessClientIDs,
    origin: any,
  ) => {
    if (
      this.isBoardReadOnly &&
      channel !== AwarenessChannel.Telepointer &&
      channel !== AwarenessChannel.Participants &&
      channel !== AwarenessChannel.Camera &&
      channel !== AwarenessChannel.Typing
    ) {
      // Read-only users can only update their telepointer, participant state or camera positions
      return;
    }

    if (this.isCurrentOrigin(origin)) {
      return;
    }

    const changedClients = added.concat(updated).concat(removed);

    this.batchedAwarenessUpdates.addUpdate({
      payload: new Map([[channel, new Set(changedClients)]]),
    });
  };

  private beforeUnloadHandler = () => {
    this.awarenessContainer.removeLocalState(
      'window unload' as unknown as TransactionOrigin,
    );
    this.batchedAwarenessUpdates.flush();
  };

  private isCurrentOrigin(origin: any) {
    const isCurrentClient =
      origin instanceof YjsOrigin &&
      origin.localOrigin === (this as unknown as TransactionOrigin);

    const isFrontendAgent =
      origin instanceof YjsOrigin && originIsFrontendAgent(origin.localOrigin);

    return isCurrentClient && !isFrontendAgent;
  }

  public destroy = () => {
    // Destroy batched update instances to force their interval to be cleared
    this.batchedUpdates.destroy();
    this.batchedAwarenessUpdates.destroy();

    // Destroy the socket
    this.socket.destroy();
    this.resetBackendAgentOperationBufferState();

    // Remove the beforeunload handler
    if (globalThis?.removeEventListener !== undefined) {
      globalThis.removeEventListener('beforeunload', this.beforeUnloadHandler);
    } else if (typeof process !== 'undefined') {
      process.off('exit', () => this.beforeUnloadHandler);
    }

    // Drop all event listeners
    this.updateTracker.off('dropped', this.onDroppedUpdate);
    this.updateTracker.off('roundtrip', this.onStepRoundtrip);
    this.awarenessContainer.off('update', this.awarenessUpdateHandler);
    this.doc.off('update', this.updateHandler);

    this.awarenessContainer.destroy();
    this.events.removeAllListeners();
  };

  private onSocketDisconnect = () => {
    debugLog('[YjsSocket:onSocketDisconnect] socket disconnected');
    // WebSocket has disconnected, so reset the last sent clock
    this.lastSentClock = null;

    // Clear any pending reconnect update — onSocketUpdateChanges will recompute
    // a fresh diff on the next connection attempt.
    this.pendingReconnectUpdate = null;

    // Reset lazy sync state on disconnect
    this.resetLazySyncState();
    this.resetBackendAgentOperationBufferState();

    this.events.emit('disconnected');
  };

  public requestSync = () => {
    void this.sendConnectSync();
  };

  private sendSyncInitMessage = () => {
    const encoder = encoding.createEncoder();
    syncProtocol.writeSyncStep1(encoder, this.doc);
    this.socket.send(
      encodeMessageV2({
        type: MessageType.SyncInit,
        data: {
          data: encoding.toUint8Array(encoder),
        },
      }),
    );
  };

  private sendConnectSync = () => {
    return this.activeCollabStrategy.sendConnectSync({
      canSync: this.checkCanSync(),
      sendSyncInit: this.sendSyncInitMessage,
      closeConnectTimeGap: () => this.closeConnectTimeGapNcs(),
    });
  };

  private onSocketConnect = () => {
    debugLog(
      '[YjsSocket:onSocketConnect] socket connected — collabStrategy=%s, hasPendingReconnectUpdate=%s',
      this.activeCollabStrategy.type,
      this.pendingReconnectUpdate !== null,
    );
    // Reset lazy sync state on connect as a defensive measure
    this.resetLazySyncState();

    // If there is a deferred offline diff from onSocketUpdateChanges, apply it now.
    // We defer to here (after C3 emits 'connected') to ensure the socket.io namespace
    // join is complete before sendThenConfirm fires. NCS silently drops sendWithAck
    // packets that arrive before the namespace join.
    if (
      this.pendingReconnectUpdate !== null &&
      this.activeCollabStrategy.shouldDeferReconnectUpdate()
    ) {
      const { update, clock } = this.pendingReconnectUpdate;
      this.pendingReconnectUpdate = null;
      debugLog(
        '[YjsSocket:onSocketConnect] applying deferred resetAndLoad, updateBytes=%d, clock=%d',
        update.byteLength,
        clock,
      );
      // Restore the observer before resetAndLoad so that subsequent addUpdate()
      // calls (e.g. new user edits after connection) trigger sendThenConfirm normally.
      this.activeCollabStrategy.restoreReconnectUpdateChanges({
        stateBridge: this.stateBridge,
        onUpdate: this.onBatchedUpdate,
      });
      this.activeCollabStrategy.prepareReconnectUpdate({
        stateBridge: this.stateBridge,
        update,
        clientID: this.doc.clientID,
        clock,
      });
      this.sendUpdate(update, UpdateMessageOrigin.None, clock);
    } else if (this.activeCollabStrategy.shouldDeferReconnectUpdate()) {
      // No pending reconnect update — still restore the observer so normal
      // editing after connection works correctly.
      this.activeCollabStrategy.restoreReconnectUpdateChanges({
        stateBridge: this.stateBridge,
        onUpdate: this.onBatchedUpdate,
      });
    }

    void this.sendConnectSync();

    const awarenessChannelsLocalState = this.getInitialAwarenessState();
    if (awarenessChannelsLocalState) {
      this.socket.sendAwareness(
        encodeMessageV2(
          this.awarenessContainer.getAwarenessMessage(
            awarenessChannelsLocalState,
            this.getCapabilities?.(),
          ),
        ),
      );
    }

    this.events.emit('connected');
  };

  private getInitialAwarenessState() {
    if (!this.isBoardReadOnly) {
      return this.awarenessContainer.getLocalStateForAllChannels();
    }

    // If the board is read-only, we only want to broadcast the telepointer, participant
    // and camera state on initial connection
    const telepointerState = this.awarenessContainer.getLocalState(
      AwarenessChannel.Telepointer,
    );
    const participantState = this.awarenessContainer.getLocalState(
      AwarenessChannel.Participants,
    );
    const cameraState = this.awarenessContainer.getLocalState(AwarenessChannel.Camera);
    const typingState =
      this.awarenessContainer.getLocalState(AwarenessChannel.Typing) ?? new Map();

    if (!telepointerState || !participantState || !cameraState || !typingState) {
      return;
    }

    return new Map<AwarenessChannel, Set<ClientId>>([
      ...telepointerState.entries(),
      ...participantState.entries(),
      ...cameraState.entries(),
      ...typingState.entries(),
    ]);
  }

  private onDocumentFetchFailed() {
    this.documentLookupFailed = true;
  }

  private onSocketFetchSnapshot = async (
    signal: AbortSignal,
  ): Promise<RemoteDocument> => {
    try {
      const remoteDoc = await this.getDocument(signal);
      this.collabStrategy = remoteDoc.collabStrategy ?? this.activeCollabStrategy;

      this.activeCollabStrategy.configureUpdateBatching({
        enableStateBridge: () => {
          this.batchedUpdates = this.stateBridge;
          this.stateBridge.onConfirmedPayload(this.onConfirmedPayload);
          this.stateBridge.onMismatch(this.onConfirmMismatch);
        },
      });

      return remoteDoc;
    } catch (err) {
      if (err instanceof DocumentLockedError) {
        // Metadata fetch rejected with 423 (locked for migration, before any
        // websocket opened). Mirror the websocket SESSION_LOCKED handling: reset
        // the reconnect backoff to keep retries at the fast fixed interval, and
        // emit sessionLocked for the "board locked" UX. Rethrow so the state
        // machine's retry loop continues.
        this.socket.resetReconnectBackoff();
        this.events.emit('sessionLocked');
        throw err;
      }
      if (
        err instanceof UnrecoverableNetworkError ||
        err instanceof BoardDocumentMissingError
      ) {
        this.onDocumentFetchFailed();
        captureExceptionForCapability(
          err,
          {
            tags: {
              captureLocation: 'yjs-socket-document-failed-fetch',
              customerImpact: CustomerImpact.CUSTOMERS,
            },
          },
          aPersonCanUseWhiteboards,
        );
      }
      throw err;
    }
  };

  private onSocketFetchSteps = async (
    remoteDoc: RemoteDocument,
    attempt: number,
    signal: AbortSignal,
  ): Promise<RemoteDocument> => {
    try {
      if (remoteDoc.headStep) {
        this.headStep = remoteDoc.headStep;

        const stepsUpdates = await this.getRemoteStepsUpdate(
          remoteDoc.headStep,
          attempt,
          signal,
          remoteDoc.collabStrategy,
        );

        // apply steps from DO onto remote document
        for (const update of stepsUpdates) {
          safeApplyUpdate(remoteDoc.document, update, undefined, (err) =>
            captureExceptionForCapability(
              err,
              {
                tags: {
                  captureLocation: 'fetch-steps-error-on-apply-update',
                  customerImpact: CustomerImpact.CUSTOMERS,
                },
              },
              aPersonCanUseWhiteboards,
            ),
          );
        }
      }

      return remoteDoc;
    } catch (err) {
      if (err instanceof UnrecoverableNetworkError) {
        this.onDocumentFetchFailed();
        captureExceptionForCapability(
          err,
          {
            tags: {
              captureLocation: 'yjs-socket-steps-failed-fetch',
              customerImpact: CustomerImpact.CUSTOMERS,
            },
          },
          aPersonCanUseWhiteboards,
        );
      }
      throw err;
    }
  };

  private onSocketApplyChanges = (relayUrl: string, remoteDoc: RemoteDocument) => {
    const localStateVector = Y.encodeStateVector(this.doc);
    const localUpdate = Y.encodeStateAsUpdate(remoteDoc.document, localStateVector);

    // apply remote changes to local doc
    Y.applyUpdate(
      this.doc,
      localUpdate,
      new YjsOrigin(YJS_DEFAULT, this as unknown as TransactionOrigin),
    );
    this.events.emit('initialLoad');

    const documentId = getDocumentId(relayUrl);
    if (this.doc.store.pendingStructs || this.doc.store.pendingDs) {
      captureException(
        new Error('Pending structs and/or deletes found in local document'),
        {
          level: 'error',
          tags: {
            captureLocation: 'apply-changes-pending-structs',
            action: 'connecting',
            documentId,
            pendingStructsMissingSize: this.doc.store.pendingStructs?.missing.size,
            pendingDeletes: this.doc.store.pendingDs?.length,
          },
        },
      );
    }

    return remoteDoc;
  };

  private onSocketUpdateChanges = (
    remoteDoc: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal,
  ): void => {
    // We don't want any pending messages at this point, as any pending updates should be
    // possible to derive from this step and the syncing step otherwise we just end up
    // with duplicate messages
    this.socket.clearPendingMessages();
    this.lastSentClock = null;

    if (this.activeCollabStrategy.shouldDeferReconnectUpdate()) {
      this.activeCollabStrategy.prepareReconnectUpdateChanges({
        stateBridge: this.stateBridge,
        onUpdate: this.onBatchedUpdate,
      });
    }

    const remoteStateVector = Y.encodeStateVector(remoteDoc.document);
    const update = encodeStateAsUpdateWithoutPendingData(this.doc, remoteStateVector);
    debugLog(
      '[YjsSocket:onSocketUpdateChanges] connectionAttempts=%d, collabStrategy=%s, updateBytes=%d, hasUnsyncedChanges=%s',
      connectionAttempts,
      this.activeCollabStrategy.type,
      update.byteLength,
      Y.parseUpdateMeta(update).from.size > 0 || Y.parseUpdateMeta(update).to.size > 0,
    );

    this.updateTracker.reset();
    this.updateTracker.on('dropped', this.onDroppedUpdate);
    this.updateTracker.on('roundtrip', this.onStepRoundtrip);

    const unsyncedChanges = Y.parseUpdateMeta(update);

    const isLargeUpdate = update.length > LARGE_STEP_THRESHOLD;
    const hasFailingConnection = connectionAttempts > 2;

    if (unsyncedChanges.to.size > 0 || unsyncedChanges.from.size > 0) {
      if (this.activeCollabStrategy.shouldDeferReconnectUpdate()) {
        const clock = Y.getState(this.doc.store, this.doc.clientID);
        this.pendingReconnectUpdate = {
          update: update as Uint8Array<ArrayBuffer>,
          clock,
        };
        // Signal the state machine to skip the 3-second grace period. When there
        // is a pending offline diff, the reconnect is intentional and we can go
        // directly to connected as soon as the namespace join completes.
        debugLog(
          '[YjsSocket:onSocketUpdateChanges] NCS mode — deferring resetAndLoad to onSocketConnect, updateBytes=%d, clock=%d',
          update.byteLength,
          clock,
        );
        this.socket.skipGracePeriod();
      } else if (isLargeUpdate || hasFailingConnection) {
        this.persistLargeStep(update, signal).catch((ex) => {
          captureExceptionForCapability(
            ex,
            {
              tags: {
                captureLocation: 'large-step-catch-all',
                customerImpact: CustomerImpact.CUSTOMERS,
              },
            },
            aPersonCanUseWhiteboards,
          );
        });
      } else {
        if (!signal.aborted) {
          this.sendUpdate(
            update,
            UpdateMessageOrigin.None,
            Y.getState(this.doc.store, this.doc.clientID),
          );
        }
      }
    }
  };

  private onSocketClosed = (code: WebSocketCodes) => {
    if (code === WebSocketCodes.SESSION_FULL) {
      this.events.emit('sessionFull');
    }
    if (code === WebSocketCodes.SESSION_LOCKED) {
      // Reset the reconnect backoff on every SESSION_LOCKED close. A locked board
      // rejects each reconnect with 4007, which would otherwise grow the debounce
      // backoff up to 60s between attempts. Resetting keeps retries at the fast
      // fixed interval so that once the board is unlocked, the next attempt (within
      // ~1s) succeeds, the WS stays open past the grace period, and 'connected'
      // fires — removing the sessionLocked policy and clearing the snackbar.
      this.socket.resetReconnectBackoff();
      // Emit sessionLocked so the UI shows the locked state. The state machine's
      // own retry loop handles reconnection attempts.
      this.events.emit('sessionLocked');
    }
  };

  private restartSocketState(reason: SocketRestartReason) {
    if (reason === 'lazy_sync_timeout') {
      this.resetLazySyncState();
    }
    recordSocketRestart({
      collabStrategyType: this.collabStrategy?.type ?? 'unknown',
      reason,
    });
    this.socket.restart();
    this.activeCollabStrategy.resetModeState({
      stateBridge: this.stateBridge,
    });
  }

  private onDroppedUpdate = () => {
    if (this.socket?.open) {
      console.warn('Detected unacknowledged updates, restarting socket');
      this.restartSocketState('dropped_update');
    }
  };

  private onConfirmMismatch = () => {
    if (this.socket?.open) {
      console.warn('Detected confirm ID mismatch, restarting socket');
      this.restartSocketState('confirm_mismatch');
    }
  };

  private onConfirmedPayload = (payload: Uint8Array<ArrayBuffer>) => {
    this.updateTracker.received(payload);
  };

  private onAckTimeout = () => {
    if (this.socket?.open) {
      console.warn('Detected ack timeout, restarting socket');
      this.restartSocketState('ack_timeout');
    }
  };

  private onStepRoundtrip = (sentAt: number, receivedAt: number) => {
    const metricName =
      this.collabStrategy?.type === 'ncs'
        ? MetricName.STEP_ROUNDTRIP_NCS
        : MetricName.STEP_ROUNDTRIP_CF;

    ReliabilityMetrics.getInstance().successWithDuration(metricName, sentAt, receivedAt);

    // Forward to external listener (e.g. socketio-simulator StatsD callback).
    // ReliabilityMetrics is a no-op in Node.js, so this is the only way to
    // get roundtrip latency out of the backend load-test runner.
    this.externalOnRoundtrip?.(sentAt, receivedAt);
  };

  addListener = this.events.addListener.bind(this.events);
  removeListener = this.events.removeListener.bind(this.events);

  private sendUpdate(payload: Uint8Array, origin: UpdateMessageOrigin, clock: number) {
    debugLog(
      '[YjsSocket:sendUpdate] socketState=%s, enqueue=%s, payloadBytes=%d, previousYjsClock=%s, currentYjsClock=%d',
      this.socket.state,
      this.checkCanUpdate(),
      payload.byteLength,
      this.lastSentClock,
      clock,
    );
    this.socket.send(
      encodeMessageV2({
        type: MessageType.Update,
        data: {
          data: payload,
          origin: origin,
          previousYjsClock: this.lastSentClock,
          currentYjsClock: clock,
        },
      }),
      this.checkCanUpdate(),
    );
    this.lastSentClock = clock;
  }

  private mergeAwareness(
    updates: Update<Map<AwarenessChannel, Set<number>>>[],
  ): Update<Map<AwarenessChannel, Set<number>>> {
    const channelToClientIdMaps = updates.map((update) => update.payload);
    const mergedChannelToClientIdMap: Map<AwarenessChannel, Set<number>> = new Map();

    for (const channelToClientIdMap of channelToClientIdMaps) {
      for (const [channel, clientIds] of channelToClientIdMap) {
        const mergedClientIds = mergedChannelToClientIdMap.get(channel) ?? new Set();

        for (const clientId of clientIds) {
          mergedClientIds.add(clientId);
        }

        mergedChannelToClientIdMap.set(channel, mergedClientIds);
      }
    }

    return {
      payload: mergedChannelToClientIdMap,
    };
  }

  get state() {
    return this.socket.state;
  }

  /** Evaluates current capabilities instead of exposing the constructor-time default. */
  get canSendUpdates(): boolean {
    return Boolean(this.checkCanUpdate());
  }

  flushUpdates() {
    this.batchedUpdates.flush();
    this.batchedAwarenessUpdates.flush();
  }

  flushTransport(ignoreAck?: boolean) {
    this.socket.flushTransport(ignoreAck);
  }

  private get isBoardReadOnly() {
    return !this.canUpdate;
  }
}
